import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { subscribe } from "@/lib/events";
import { Award, Loader2, AlertTriangle, CheckCircle2, HelpCircle, Target, Star, Plus, RefreshCw, Wand2 } from "lucide-react";
import PastPerformanceSlideOver from "@/components/PastPerformanceSlideOver";

/**
 * PastPerformanceMatchCard — read-only preview of which past performance
 * records the AI will cite when the operator hits "Generate proposal".
 * Fetches on mount, does NOT consume credits. Renders empty state + CTA
 * when the library has zero records so operators can seed references
 * BEFORE spending a credit on generation.
 *
 * Props:
 *   noticeId — opportunity notice_id (required)
 */
export default function PastPerformanceMatchCard({ noticeId }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(false); // brief highlight after rescore
  const [quickOpen, setQuickOpen] = useState(false);

  const fetchPreview = async ({ isRescore = false } = {}) => {
    if (!isRescore) setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/past-performance/match/${noticeId}`);
      setPreview(r.data);
      if (isRescore) {
        setFlash(true);
        setTimeout(() => setFlash(false), 1600);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load match preview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      await fetchPreview();
      if (!alive) return;
    })();
    // Rescore whenever the operator adds/edits/deletes/bulk-imports a record
    // in this tab OR any other tab of the app.
    const unsub = subscribe("past_performance:changed", () => {
      if (alive) fetchPreview({ isRescore: true });
    });
    return () => {
      alive = false;
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticeId]);

  if (loading) {
    return (
      <div className="bg-[#0D1424] border border-white/10 p-5" data-testid="pp-match-loading">
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Ranking your past performance…
        </div>
      </div>
    );
  }

  if (error) return null; // silent — this is an advisory card

  const total = preview?.total_in_library ?? 0;
  const items = preview?.items || [];
  const top = preview?.top_score ?? 0;

  // Empty state — no records at all in the library
  if (total === 0) {
    return (
      <>
        <div
          className="bg-[#0D1424] border border-amber-400/40 p-5"
          data-testid="pp-match-empty"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-400/15 text-amber-300 flex items-center justify-center shrink-0">
              <Award className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="mono-label text-amber-300 mb-1">// PAST PERFORMANCE</div>
              <div className="font-display font-bold text-lg text-white">
                No past performance yet — your proposal will feel thin
              </div>
              <p className="text-sm text-slate-400 mt-1">
                Add relevant projects so the AI can cite them by name inside the proposal. Federal evaluators score
                relevant experience heavily.
              </p>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <button
                  onClick={() => setQuickOpen(true)}
                  className="inline-flex items-center gap-2 text-xs font-mono font-bold tracking-widest bg-amber-400 text-slate-900 hover:bg-amber-300 px-4 py-2 rounded-full"
                  data-testid="pp-match-quick-add-empty"
                >
                  <Wand2 className="w-3.5 h-3.5" />
                  QUICK ADD HERE
                </button>
                <Link
                  to="/past-performance"
                  className="inline-flex items-center gap-2 text-xs font-mono font-bold tracking-widest text-slate-400 hover:text-cyan-300 border border-white/10 hover:border-cyan-400/50 px-4 py-2 rounded-full"
                  data-testid="pp-match-add-cta"
                >
                  <Plus className="w-3.5 h-3.5" />
                  OPEN LIBRARY
                </Link>
              </div>
            </div>
          </div>
        </div>
        <PastPerformanceSlideOver open={quickOpen} onClose={() => setQuickOpen(false)} noticeId={noticeId} />
      </>
    );
  }

  // Score → chip class + label
  const chip = (score) => {
    if (score >= 80) return { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40", label: "HIGH", icon: CheckCircle2 };
    if (score >= 50) return { cls: "bg-amber-500/15 text-amber-300 border-amber-400/40", label: "MED", icon: AlertTriangle };
    return { cls: "bg-rose-500/15 text-rose-300 border-rose-400/40", label: "LOW", icon: HelpCircle };
  };

  // Top-level verdict banner
  const verdict = (() => {
    if (top >= 80)
      return {
        cls: "border-emerald-400/40 text-emerald-200 bg-emerald-500/5",
        msg: `Strong match — top reference scores ${top}. AI will cite it prominently.`,
        icon: CheckCircle2,
        tid: "pp-match-verdict-strong",
      };
    if (top >= 50)
      return {
        cls: "border-amber-400/40 text-amber-200 bg-amber-500/5",
        msg: `Decent match — top score ${top}. Consider adding a more relevant reference for a killer proposal.`,
        icon: AlertTriangle,
        tid: "pp-match-verdict-decent",
      };
    return {
      cls: "border-rose-400/40 text-rose-200 bg-rose-500/5",
      msg: `Weak match — top score ${top}. Add relevant past performance BEFORE generating the proposal.`,
      icon: HelpCircle,
      tid: "pp-match-verdict-weak",
    };
  })();

  const money = (v) =>
    v == null || isNaN(Number(v)) ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <>
      <div
        className={`bg-[#0D1424] border p-5 transition-all ${flash ? "border-emerald-400/70 ring-2 ring-emerald-400/30" : "border-white/10"}`}
        data-testid="pp-match-card"
      >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="mono-label text-amber-300 mb-1 flex items-center gap-2">
            <Award className="w-3.5 h-3.5" />
            // AI WILL CITE THESE
            {flash && (
              <span
                className="ml-1 inline-flex items-center gap-1 text-[10px] font-mono font-bold tracking-widest bg-emerald-500/15 text-emerald-300 border border-emerald-400/40 px-2 py-0.5 rounded-full"
                data-testid="pp-match-rescored-flash"
              >
                <RefreshCw className="w-3 h-3" />
                RESCORED
              </span>
            )}
          </div>
          <div className="font-display font-bold text-lg text-white">
            Past-performance match preview
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Top {items.length} of {total} record{total === 1 ? "" : "s"} in your library, ranked by scope similarity,
            NAICS overlap, and keyword hits.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setQuickOpen(true)}
            className="text-xs font-mono font-bold tracking-widest bg-amber-400 text-slate-900 hover:bg-amber-300 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5"
            data-testid="pp-match-quick-add"
          >
            <Wand2 className="w-3.5 h-3.5" />
            QUICK ADD
          </button>
          <button
            onClick={() => fetchPreview({ isRescore: true })}
            className="text-slate-400 hover:text-cyan-300 border border-white/10 hover:border-cyan-400/50 rounded-full p-1.5"
            title="Rescore now"
            data-testid="pp-match-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <Link
            to="/past-performance"
            className="text-xs font-mono font-bold tracking-widest text-slate-400 hover:text-cyan-300 border border-white/10 hover:border-cyan-400/50 rounded-full px-3 py-1.5"
            data-testid="pp-match-manage"
          >
            MANAGE LIBRARY
          </Link>
        </div>
      </div>

      {/* Verdict banner */}
      <div
        className={`border rounded-lg px-4 py-3 mb-4 flex items-start gap-2.5 text-sm ${verdict.cls}`}
        data-testid={verdict.tid}
      >
        <verdict.icon className="w-4 h-4 mt-0.5 shrink-0" />
        <div>{verdict.msg}</div>
      </div>

      {/* Rows */}
      <ul className="space-y-2" data-testid="pp-match-list">
        {items.map((it) => {
          const c = chip(it.match_score);
          return (
            <li
              key={it.pp_id}
              className="border border-white/5 bg-white/[0.02] rounded-lg px-3 py-3 flex items-start gap-3"
              data-testid={`pp-match-row-${it.pp_id}`}
            >
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-mono font-bold tracking-widest border rounded-full px-2 py-0.5 shrink-0 ${c.cls}`}
                title={`Match score ${it.match_score}`}
                data-testid={`pp-match-chip-${it.pp_id}`}
              >
                <c.icon className="w-3 h-3" />
                {c.label} {it.match_score}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display font-bold text-white truncate">{it.project_title}</div>
                  {it.cpars_rating && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-mono font-bold tracking-widest text-amber-300">
                      <Star className="w-3 h-3" />
                      {it.cpars_rating}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-400">
                  {it.customer || "Customer n/a"}
                  {it.contract_value != null && (
                    <>
                      {" · "}
                      <span className="text-emerald-300 font-mono font-bold">{money(it.contract_value)}</span>
                    </>
                  )}
                  {(it.period_start || it.period_end) && (
                    <> · {(it.period_start || "?") + " – " + (it.period_end || "?")}</>
                  )}
                </div>
                {it.scope_preview && (
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">{it.scope_preview}</div>
                )}
                {(it.naics_codes?.length > 0 || it.keywords?.length > 0) && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(it.naics_codes || []).map((n) => (
                      <span
                        key={`n-${n}`}
                        className="text-[10px] font-mono font-bold tracking-widest bg-indigo-500/15 text-indigo-300 px-2 py-0.5 rounded-full"
                      >
                        NAICS {n}
                      </span>
                    ))}
                    {(it.keywords || []).map((k) => (
                      <span
                        key={`k-${k}`}
                        className="text-[10px] font-mono font-bold tracking-widest bg-white/5 text-slate-300 px-2 py-0.5 rounded-full"
                      >
                        {k}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      </div>
      <PastPerformanceSlideOver open={quickOpen} onClose={() => setQuickOpen(false)} noticeId={noticeId} />
    </>
  );
}
